import { useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest } from '@/lib/api';
import { normalizeRemoteMediaUrl } from '@/lib/media';
import AddChurchWizard from '@/pages/add-church/components/AddChurchWizard';
import { getChurchImageUrl } from '@/utils/churchImages';

type Eglise = {
  id: number;
  nom: string;
  diocese?: string | null;
  image?: string | null;
  images?: string[] | null;
  displayImage?: string | null;
  video?: string | null;
  adresse?: string | null;
  ville?: string | null;
  description?: string | null;
  messe_dimanche?: unknown[] | null;
  horaire_messe?: unknown[] | Record<string, unknown> | null;
  horaire_confession?: unknown[] | Record<string, unknown> | null;
  horaire_bureau?: string | null;
  bureau_schedule?: unknown[] | Record<string, unknown> | null;
  contacts?: unknown[] | Record<string, unknown> | null;
  equipe?: unknown[] | Record<string, unknown> | null;
  lat?: number | null;
  lng?: number | null;
  status?: string | null;
  statut?: string | null;
};

type DetailTab = 'info' | 'map' | 'team' | 'calendar';
type ParsedContact = { label: string; value: string };
type ParsedTeamMember = { name: string; role: string; image?: string | null };
type ParsedSchedule = { day: string; time: string };
type ImageDraft = { id: string; url: string; file: File | null };
type EditForm = {
  nom: string;
  diocese: string;
  ville: string;
  adresse: string;
  description: string;
  image: string;
  imageFile: File | null;
  images: ImageDraft[];
  lat: string;
  lng: string;
  status: string;
  horaire_messe: ParsedSchedule[];
  horaire_confession: ParsedSchedule[];
  bureau_schedule: ParsedSchedule[];
  contacts: ParsedContact[];
  equipe: ParsedTeamMember[];
};

const PAGE_SIZE_OPTIONS = [9, 18, 30] as const;

const CARD_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=church%20cathedral%20exterior%20bright%20sky%20clean%20architecture&width=800&height=500&seq=eglise-admin-card&orientation=landscape';

const normalizeChurchMedia = (eglise: Pick<Eglise, 'nom' | 'diocese' | 'image'>) =>
  getChurchImageUrl(eglise.nom, eglise.image, eglise.diocese) || CARD_FALLBACK_IMAGE;

function ChurchImageFrame({ image, alt, compact = false }: { image?: string | null; alt: string; compact?: boolean }) {
  if (!image) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7e2] via-[#fffaf0] to-[#f4f7ff]">
        <div className="flex flex-col items-center gap-3 px-6 text-center text-[#8c97ad]">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-sm">
            <i className="ri-building-4-line text-2xl text-gold"></i>
          </div>
          <div>
            <p className={`font-semibold text-navy ${compact ? 'text-sm' : 'text-base'}`}>Image non disponible</p>
            {!compact ? <p className="mt-1 text-xs">Ajoute une photo pour cette église depuis l'administration.</p> : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={image}
      alt={alt}
      className="h-full w-full object-cover"
      onError={(event) => {
        event.currentTarget.style.display = 'none';
        const nextElement = event.currentTarget.nextElementSibling as HTMLElement | null;
        if (nextElement) {
          nextElement.style.display = 'flex';
        }
      }}
    />
  );
}

const getChurchStatus = (eglise: Eglise) => {
  const value = (eglise.status ?? eglise.statut ?? 'active').toString().toLowerCase();
  if (value === 'pending' || value === 'en_attente') return 'pending';
  if (value === 'suspended' || value === 'suspendue' || value === 'suspendu') return 'suspended';
  if (value === 'deleted' || value === 'corbeille') return 'deleted';
  return 'active';
};

const getStatusLabel = (status: string) => {
  if (status === 'pending') return 'En attente';
  if (status === 'suspended') return 'Suspendue';
  if (status === 'deleted') return 'Corbeille';
  return 'Active';
};

const getStatusClass = (status: string) => {
  if (status === 'pending') return 'bg-amber-100 text-amber-700';
  if (status === 'suspended') return 'bg-red-100 text-red-600';
  if (status === 'deleted') return 'bg-slate-100 text-slate-600';
  return 'bg-emerald-100 text-emerald-700';
};

const normalizeCollection = (value: Eglise['contacts'] | Eglise['equipe']) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const normalizeScheduleCollection = (value: unknown[] | Record<string, unknown> | null | undefined) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const extractLeader = (eglise: Eglise) => {
  const fromContacts = normalizeCollection(eglise.contacts).find((entry) => typeof entry === 'string');
  if (typeof fromContacts === 'string' && fromContacts.trim()) return fromContacts;

  const fromTeam = normalizeCollection(eglise.equipe).find((entry) => {
    if (typeof entry === 'string') return entry.trim().length > 0;
    if (entry && typeof entry === 'object') {
      const candidate = (entry as Record<string, unknown>).name ?? (entry as Record<string, unknown>).nom ?? (entry as Record<string, unknown>).title;
      return typeof candidate === 'string' && candidate.trim().length > 0;
    }
    return false;
  });

  if (typeof fromTeam === 'string') return fromTeam;
  if (fromTeam && typeof fromTeam === 'object') {
    const candidate = (fromTeam as Record<string, unknown>).name ?? (fromTeam as Record<string, unknown>).nom ?? (fromTeam as Record<string, unknown>).title;
    if (typeof candidate === 'string') return candidate;
  }

  return 'Responsable non renseigné';
};

const parseContacts = (eglise: Eglise): ParsedContact[] =>
  normalizeCollection(eglise.contacts)
    .map((entry) => {
      if (typeof entry === 'string') {
        return { label: 'Contact', value: entry };
      }

      if (entry && typeof entry === 'object') {
        const raw = entry as Record<string, unknown>;
        const label = raw.label ?? raw.type ?? raw.nom ?? 'Contact';
        const value = raw.valeur ?? raw.value ?? raw.contact ?? raw.numero ?? '';
        return {
          label: String(label || 'Contact'),
          value: String(value || ''),
        };
      }

      return null;
    })
    .filter((entry): entry is ParsedContact => Boolean(entry?.value.trim()));

const parseTeam = (eglise: Eglise): ParsedTeamMember[] =>
  normalizeCollection(eglise.equipe)
    .map((entry): ParsedTeamMember | null => {
      if (typeof entry === 'string') {
        return { name: entry, role: 'Membre de l’équipe' };
      }

      if (entry && typeof entry === 'object') {
        const raw = entry as Record<string, unknown>;
        const name = raw.nom ?? raw.name ?? raw.title ?? '';
        const role = raw.role ?? raw.fonction ?? 'Membre de l’équipe';
        const image = raw.image;
        return {
          name: String(name || ''),
          role: String(role || 'Membre de l’équipe'),
          image: typeof image === 'string' ? image : null,
        };
      }

      return null;
    })
    .filter((entry): entry is ParsedTeamMember => Boolean(entry?.name.trim()));

const parseSchedule = (value: unknown[] | Record<string, unknown> | null | undefined): ParsedSchedule[] =>
  normalizeScheduleCollection(value)
    .map((entry) => {
      if (typeof entry === 'string') {
        return { day: 'Jour', time: entry };
      }

      if (entry && typeof entry === 'object') {
        const raw = entry as Record<string, unknown>;
        const day = raw.jour ?? raw.day ?? raw.label ?? 'Jour';
        const time = raw.heure ?? raw.time ?? raw.valeur ?? raw.value ?? '';
        return {
          day: String(day || 'Jour'),
          time: String(time || ''),
        };
      }

      return null;
    })
    .filter((entry): entry is ParsedSchedule => Boolean(entry?.time.trim()));

const blankSchedule = (): ParsedSchedule => ({ day: '', time: '' });
const blankContact = (): ParsedContact => ({ label: 'Téléphone', value: '' });
const blankTeamMember = (): ParsedTeamMember => ({ name: '', role: 'Curé', image: '' });

const ensureEditableRows = <T,>(items: T[], fallback: () => T) => (items.length > 0 ? items : [fallback()]);
const makeImageDraft = (url: string, file: File | null = null): ImageDraft => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  url,
  file,
});

const imageDraftsFromChurch = (eglise: Eglise): ImageDraft[] => {
  const values = [
    ...(Array.isArray(eglise.images) ? eglise.images : []),
    eglise.image ?? '',
  ]
    .map((image) => normalizeRemoteMediaUrl(image) ?? '')
    .filter(Boolean);
  const uniqueValues = Array.from(new Set(values)).slice(0, 5);

  return uniqueValues.map((image) => makeImageDraft(image));
};

const editFormFromChurch = (eglise: Eglise): EditForm => ({
  nom: eglise.nom ?? '',
  diocese: eglise.diocese ?? '',
  ville: eglise.ville ?? '',
  adresse: eglise.adresse ?? '',
  description: eglise.description ?? '',
  image: eglise.image ?? '',
  imageFile: null,
  images: imageDraftsFromChurch(eglise),
  lat: eglise.lat != null ? String(eglise.lat) : '',
  lng: eglise.lng != null ? String(eglise.lng) : '',
  status: getChurchStatus(eglise),
  horaire_messe: ensureEditableRows(parseSchedule(eglise.horaire_messe), blankSchedule),
  horaire_confession: ensureEditableRows(parseSchedule(eglise.horaire_confession), blankSchedule),
  bureau_schedule: ensureEditableRows(parseSchedule(eglise.bureau_schedule), blankSchedule),
  contacts: ensureEditableRows(parseContacts(eglise), blankContact),
  equipe: ensureEditableRows(parseTeam(eglise), blankTeamMember),
});

const formatGps = (eglise: Eglise) =>
  eglise.lat != null && eglise.lng != null ? `${eglise.lat}, ${eglise.lng}` : 'Coordonnées non renseignées';

const buildAddress = (eglise: Eglise) =>
  [eglise.adresse, eglise.ville].filter(Boolean).join(', ') || 'Adresse non renseignée';

const getMapEmbedUrl = (eglise: Eglise) => {
  if (eglise.lat != null && eglise.lng != null) {
    return `https://maps.google.com/maps?q=${eglise.lat},${eglise.lng}&z=16&output=embed`;
  }

  return `https://maps.google.com/maps?q=${encodeURIComponent([eglise.adresse, eglise.ville, eglise.nom].filter(Boolean).join(', '))}&z=15&output=embed`;
};

const getMapExternalUrl = (eglise: Eglise) => {
  if (eglise.lat != null && eglise.lng != null) {
    return `https://www.google.com/maps?q=${eglise.lat},${eglise.lng}`;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([eglise.adresse, eglise.ville, eglise.nom].filter(Boolean).join(', '))}`;
};

export default function EglisesManagement() {
  const [eglises, setEglises] = useState<Eglise[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'suspended' | 'deleted'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(9);
  const [isAddingChurch, setIsAddingChurch] = useState(false);
  const [selectedEglise, setSelectedEglise] = useState<Eglise | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('info');
  const [editingEglise, setEditingEglise] = useState<Eglise | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const editGalleryInputRef = useRef<HTMLInputElement | null>(null);
  const replaceGalleryInputRef = useRef<HTMLInputElement | null>(null);

  const loadEglises = async () => {
    setLoading(true);
    try {
      const response = await apiRequest<Eglise[]>('/admin/eglises?include_deleted=1');
      setEglises(response.map((eglise) => {
        const image = normalizeRemoteMediaUrl(eglise.image);
        const images = Array.isArray(eglise.images)
          ? eglise.images.map((item) => normalizeRemoteMediaUrl(item) ?? '').filter(Boolean).slice(0, 5)
          : [];

        return {
          ...eglise,
          image,
          images,
          displayImage: images[0] ?? normalizeChurchMedia({ ...eglise, image }),
          video: normalizeRemoteMediaUrl(eglise.video),
        };
      }));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les églises.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEglises();
  }, []);

  const filteredEglises = useMemo(() => {
    const term = search.trim().toLowerCase();
    return eglises.filter((eglise) => {
      const status = getChurchStatus(eglise);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;
      const matchesSearch = !term || [eglise.nom, eglise.ville, eglise.diocese, eglise.adresse, eglise.description, extractLeader(eglise)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));

      return matchesStatus && matchesSearch;
    });
  }, [eglises, search, statusFilter]);

  const total = filteredEglises.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const paginatedEglises = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredEglises.slice(start, start + pageSize);
  }, [currentPage, filteredEglises, pageSize]);

  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(lastPage, currentPage + 2);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }, [currentPage, lastPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize, statusFilter]);

  useEffect(() => {
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, lastPage]);

  const stats = useMemo(() => ({
    total,
    active: filteredEglises.filter((eglise) => getChurchStatus(eglise) === 'active').length,
    pending: filteredEglises.filter((eglise) => getChurchStatus(eglise) === 'pending').length,
    suspended: filteredEglises.filter((eglise) => getChurchStatus(eglise) === 'suspended').length,
    deleted: filteredEglises.filter((eglise) => getChurchStatus(eglise) === 'deleted').length,
  }), [filteredEglises, total]);

  const openEdit = (eglise: Eglise) => {
    setEditingEglise(eglise);
    setEditForm(editFormFromChurch(eglise));
  };

  const openDetails = (eglise: Eglise) => {
    setSelectedEglise(eglise);
    setDetailTab('info');
  };

  const addGalleryFiles = (files: FileList | null) => {
    if (!files || !editForm) return;

    const remaining = 5 - editForm.images.length;
    if (remaining <= 0) {
      setActionMessage({ type: 'error', text: 'Une église peut avoir au maximum 5 photos.' });
      return;
    }

    const nextImages = Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, remaining)
      .map((file) => makeImageDraft(URL.createObjectURL(file), file));

    setEditForm((current) => current ? ({
      ...current,
      images: [...current.images, ...nextImages].slice(0, 5),
      image: current.image || nextImages[0]?.url || '',
      imageFile: current.imageFile,
    }) : current);
  };

  const replaceGalleryFiles = (files: FileList | null) => {
    if (!files || !editForm) return;

    const nextImages = Array.from(files)
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, 5)
      .map((file) => makeImageDraft(URL.createObjectURL(file), file));

    if (nextImages.length === 0) {
      return;
    }

    setEditForm((current) => {
      if (!current) return current;

      current.images.forEach((image) => {
        if (image.file && image.url.startsWith('blob:')) {
          URL.revokeObjectURL(image.url);
        }
      });

      return {
        ...current,
        images: nextImages,
        image: nextImages[0]?.url ?? '',
        imageFile: null,
      };
    });
  };

  const removeGalleryImage = (id: string) => {
    setEditForm((current) => {
      if (!current) return current;
      const removed = current.images.find((image) => image.id === id);
      if (removed?.file && removed.url.startsWith('blob:')) {
        URL.revokeObjectURL(removed.url);
      }

      const images = current.images.filter((image) => image.id !== id);
      const firstImage = images[0];
      return {
        ...current,
        images,
        image: firstImage?.url ?? '',
        imageFile: current.imageFile && images.some((image) => image.file === current.imageFile) ? current.imageFile : null,
      };
    });
  };

  const handleUpdateChurch = async () => {
    if (!editingEglise || !editForm) return;
    setSubmitting(true);
    try {
      const cleanSchedule = (items: ParsedSchedule[]) =>
        items
          .map((item) => ({ jour: item.day.trim(), heure: item.time.trim() }))
          .filter((item) => item.jour && item.heure);
      const cleanContacts = editForm.contacts
        .map((item) => ({ type: item.label.trim() || 'Contact', value: item.value.trim() }))
        .filter((item) => item.value);
      const cleanTeam = editForm.equipe
        .map((item) => ({
          nom: item.name.trim(),
          role: item.role.trim() || 'Membre de l’équipe',
          image: item.image?.trim() || '',
        }))
        .filter((item) => item.nom);
      const galleryImages = editForm.images.slice(0, 5);
      const galleryUrls = galleryImages
        .filter((image) => !image.file)
        .map((image) => image.url.trim())
        .filter(Boolean);
      const galleryFiles = galleryImages
        .map((image) => image.file)
        .filter((file): file is File => file instanceof File);
      const primaryImage = galleryImages[0];

      const payload = {
        nom: editForm.nom.trim(),
        diocese: editForm.diocese.trim(),
        ville: editForm.ville.trim(),
        adresse: editForm.adresse.trim(),
        description: editForm.description.trim(),
        image: primaryImage?.file ? '' : (primaryImage?.url ?? editForm.image).trim(),
        images: galleryUrls,
        lat: editForm.lat.trim() ? Number(editForm.lat) : null,
        lng: editForm.lng.trim() ? Number(editForm.lng) : null,
        status: editForm.status,
        horaire_messe: cleanSchedule(editForm.horaire_messe),
        horaire_confession: cleanSchedule(editForm.horaire_confession),
        bureau_schedule: cleanSchedule(editForm.bureau_schedule),
        horaire_bureau: cleanSchedule(editForm.bureau_schedule).map((entry) => `${entry.jour}: ${entry.heure}`).join(' | '),
        contacts: cleanContacts,
        equipe: cleanTeam,
      };

      if (editForm.imageFile || galleryFiles.length > 0) {
        const formData = new FormData();
        formData.append('_method', 'PUT');
        Object.entries(payload).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            if (value.length === 0) {
              formData.append(`${key}[]`, '');
              return;
            }

            value.forEach((entry, index) => {
              if (typeof entry === 'string') {
                formData.append(`${key}[${index}]`, entry);
                return;
              }

              Object.entries(entry).forEach(([entryKey, entryValue]) => {
                formData.append(`${key}[${index}][${entryKey}]`, String(entryValue ?? ''));
              });
            });
            return;
          }

          if (value !== null) {
            formData.append(key, String(value));
          }
        });
        if (editForm.imageFile) formData.append('image_file', editForm.imageFile);
        galleryFiles.forEach((file) => formData.append('image_files[]', file));

        await apiRequest(`/eglises/${editingEglise.id}`, {
          method: 'POST',
          body: formData,
        });
      } else {
        await apiRequest(`/eglises/${editingEglise.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      setActionMessage({ type: 'success', text: 'Église mise à jour.' });
      setEditingEglise(null);
      setEditForm(null);
      await loadEglises();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Impossible de modifier cette église.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleSuspend = async (eglise: Eglise) => {
    const nextStatus = getChurchStatus(eglise) === 'suspended' ? 'active' : 'suspended';
    try {
      await apiRequest(`/eglises/${eglise.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus }),
      });
      setActionMessage({ type: 'success', text: nextStatus === 'suspended' ? 'Église suspendue.' : 'Église réactivée.' });
      await loadEglises();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Impossible de changer le statut.' });
    }
  };

  const handleDeleteChurch = async (eglise: Eglise) => {
    const confirmed = window.confirm(`Mettre "${eglise.nom}" dans la corbeille ?`);
    if (!confirmed) return;

    try {
      await apiRequest(`/eglises/${eglise.id}`, { method: 'DELETE' });
      setActionMessage({ type: 'success', text: 'Église déplacée dans la corbeille.' });
      if (selectedEglise?.id === eglise.id) setSelectedEglise(null);
      await loadEglises();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Impossible de supprimer cette église.' });
    }
  };

  const handleRestoreChurch = async (eglise: Eglise) => {
    const confirmed = window.confirm(`Restaurer "${eglise.nom}" ?`);
    if (!confirmed) return;

    try {
      await apiRequest(`/eglises/${eglise.id}/restore`, { method: 'POST' });
      setActionMessage({ type: 'success', text: 'Église restaurée.' });
      await loadEglises();
    } catch (err) {
      setActionMessage({ type: 'error', text: err instanceof Error ? err.message : 'Impossible de restaurer cette église.' });
    }
  };

  return (
    <div className="space-y-6">
      {isAddingChurch ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-navy">Ajouter une église</h2>
              <p className="text-sm text-gray-500">Le formulaire s’affiche directement dans le dashboard admin.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsAddingChurch(false)}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              Retour à la liste
            </button>
          </div>

          <AddChurchWizard embedded onClose={() => setIsAddingChurch(false)} />
        </div>
      ) : null}

      {!isAddingChurch ? (
        <>
      {actionMessage ? (
        <div className={`rounded-2xl px-4 py-3 text-sm ${actionMessage.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {actionMessage.text}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: 'Total Églises', value: stats.total, icon: 'ri-building-4-line', color: 'bg-navy text-white' },
          { label: 'Actives', value: stats.active, icon: 'ri-checkbox-circle-line', color: 'bg-emerald-500 text-white' },
          { label: 'En attente', value: stats.pending, icon: 'ri-time-line', color: 'bg-amber-500 text-white' },
          { label: 'Corbeille', value: stats.deleted, icon: 'ri-delete-bin-line', color: 'bg-slate-500 text-white' },
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-4">
              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.color}`}>
                <i className={`${card.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{card.value}</p>
                <p className="text-sm text-gray-500">{card.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher par nom, ville, pasteur, dénomination..."
              className="w-full rounded-2xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30"
            />
          </div>
          <button
            type="button"
            onClick={() => setIsAddingChurch(true)}
            className="rounded-2xl bg-navy px-5 py-3 text-sm font-semibold text-white"
          >
            <i className="ri-add-line mr-2"></i>
            Ajouter une église
          </button>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
            className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actives</option>
            <option value="pending">En attente</option>
            <option value="suspended">Suspendues</option>
            <option value="deleted">Corbeille</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-gray-100 bg-white p-8 text-sm text-gray-400">Chargement des églises...</div>
      ) : null}
      {!loading && error ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-sm text-red-600">{error}</div>
      ) : null}

      {!loading && !error ? (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          {paginatedEglises.map((eglise) => {
            const status = getChurchStatus(eglise);
            const leader = extractLeader(eglise);
            return (
              <div key={eglise.id} className="overflow-hidden rounded-3xl border border-[#eadfcb] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                <div className="relative h-44 overflow-hidden bg-cream">
                  <ChurchImageFrame image={eglise.displayImage ?? eglise.image} alt={eglise.nom} compact />
                  <div className="hidden h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7e2] via-[#fffaf0] to-[#f4f7ff]">
                    <div className="flex flex-col items-center gap-3 px-6 text-center text-[#8c97ad]">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-sm">
                        <i className="ri-building-4-line text-2xl text-gold"></i>
                      </div>
                      <p className="text-sm font-semibold text-navy">Image non disponible</p>
                    </div>
                  </div>
                  <span className={`absolute right-3 top-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(status)}`}>
                    {getStatusLabel(status)}
                  </span>
                </div>
                <div className="space-y-3 p-4">
                  <div>
                    <h3 className="text-xl font-bold text-navy">{eglise.nom}</h3>
                    <p className="text-sm text-gray-500">{eglise.diocese || 'Église'}</p>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-user-star-line text-gold"></i>
                      <span>{leader}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-gold"></i>
                      <span>{[eglise.ville, eglise.adresse].filter(Boolean).join(', ') || 'Adresse non renseignée'}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-compass-3-line text-emerald-600"></i>
                      <span>{eglise.lat && eglise.lng ? `${eglise.lat}, ${eglise.lng}` : 'Coordonnées non renseignées'}</span>
                    </p>
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <p className="line-clamp-3 text-sm text-gray-500">{eglise.description || 'Aucune description disponible pour cette église.'}</p>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
                    <button type="button" onClick={() => openDetails(eglise)} className="flex items-center gap-1.5 text-slate-500 hover:text-navy">
                      <i className="ri-eye-line"></i>
                      Voir
                    </button>
                    {status === 'deleted' ? (
                      <button type="button" onClick={() => void handleRestoreChurch(eglise)} className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700">
                        <i className="ri-arrow-go-back-line"></i>
                        Restaurer
                      </button>
                    ) : (
                      <>
                        <button type="button" onClick={() => openEdit(eglise)} className="flex items-center gap-1.5 text-slate-500 hover:text-navy">
                          <i className="ri-pencil-line"></i>
                          Modifier
                        </button>
                        <button type="button" onClick={() => void handleToggleSuspend(eglise)} className="flex items-center gap-1.5 text-red-500 hover:text-red-600">
                          <i className="ri-forbid-2-line"></i>
                          {getChurchStatus(eglise) === 'suspended' ? 'Réactiver' : 'Suspendre'}
                        </button>
                        <button type="button" onClick={() => void handleDeleteChurch(eglise)} className="flex items-center gap-1.5 text-red-400 hover:text-red-600">
                          <i className="ri-delete-bin-line"></i>
                          Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {!loading && !error && filteredEglises.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-400">
          Aucune église trouvée pour cette recherche.
        </div>
      ) : null}
      {!loading && !error && total > 0 ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-5 py-4 shadow-[0_10px_30px_rgba(15,23,42,0.04)] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <p className="text-sm text-gray-500">
              Page {currentPage} sur {lastPage} - {total} églises
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="eglises-page-size" className="text-sm text-gray-500">
                Par page
              </label>
              <select
                id="eglises-page-size"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value) as (typeof PAGE_SIZE_OPTIONS)[number])}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-gold/30"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Précédent
            </button>
            {pageNumbers[0] > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600"
                >
                  1
                </button>
                {pageNumbers[0] > 2 ? <span className="px-1 text-sm text-gray-400">...</span> : null}
              </>
            ) : null}
            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                  page === currentPage
                    ? 'bg-navy text-white'
                    : 'border border-gray-200 text-gray-600'
                }`}
              >
                {page}
              </button>
            ))}
            {pageNumbers[pageNumbers.length - 1] < lastPage ? (
              <>
                {pageNumbers[pageNumbers.length - 1] < lastPage - 1 ? <span className="px-1 text-sm text-gray-400">...</span> : null}
                <button
                  type="button"
                  onClick={() => setCurrentPage(lastPage)}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600"
                >
                  {lastPage}
                </button>
              </>
            ) : null}
            <button
              type="button"
              disabled={currentPage >= lastPage}
              onClick={() => setCurrentPage((page) => Math.min(lastPage, page + 1))}
              className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      ) : null}
        </>
      ) : null}

      {selectedEglise ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedEglise(null)}>
          <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="relative h-52 flex-shrink-0 overflow-hidden bg-cream">
              <ChurchImageFrame image={selectedEglise.displayImage ?? selectedEglise.image} alt={selectedEglise.nom} />
              <div className="hidden h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7e2] via-[#fffaf0] to-[#f4f7ff]">
                <div className="flex flex-col items-center gap-3 px-6 text-center text-[#8c97ad]">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-sm">
                    <i className="ri-building-4-line text-3xl text-gold"></i>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-navy">Image non disponible</p>
                    <p className="mt-1 text-xs">Ajoute une photo pour cette église depuis l'administration.</p>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent"></div>
              <button onClick={() => setSelectedEglise(null)} className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white backdrop-blur-sm hover:bg-white/35">
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="mb-1 text-sm text-white/75">{selectedEglise.diocese || 'Église'}</p>
                    <h3 className="text-2xl font-bold text-white">{selectedEglise.nom}</h3>
                    <p className="mt-1 text-sm text-white/85">{buildAddress(selectedEglise)}</p>
                  </div>
                  <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(getChurchStatus(selectedEglise))}`}>
                    {getStatusLabel(getChurchStatus(selectedEglise))}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap border-b border-gray-100 bg-white">
              {[
                { key: 'info', label: 'Informations', icon: 'ri-information-line' },
                { key: 'map', label: 'Carte', icon: 'ri-map-pin-line' },
                { key: 'team', label: 'Équipe', icon: 'ri-team-line' },
                { key: 'calendar', label: 'Calendrier', icon: 'ri-calendar-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setDetailTab(tab.key as DetailTab)}
                  className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold ${
                    detailTab === tab.key ? 'border-gold bg-amber-50/60 text-gold-dark' : 'border-transparent text-gray-500 hover:text-navy'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {detailTab === 'info' ? (
                <div className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      { icon: 'ri-user-star-line', label: 'Responsable', value: extractLeader(selectedEglise) },
                      { icon: 'ri-community-line', label: 'Diocèse', value: selectedEglise.diocese || 'Non renseigné' },
                      { icon: 'ri-map-pin-line', label: 'Adresse', value: buildAddress(selectedEglise) },
                      { icon: 'ri-compass-3-line', label: 'Coordonnées GPS', value: formatGps(selectedEglise) },
                    ].map((item) => (
                      <div key={item.label} className="rounded-2xl bg-gray-50 p-4">
                        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-navy/10 text-navy">
                          <i className={item.icon}></i>
                        </div>
                        <p className="text-xs uppercase tracking-[0.18em] text-gray-400">{item.label}</p>
                        <p className="mt-1 text-sm font-semibold text-navy">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {parseContacts(selectedEglise).length > 0 ? (
                    <div className="rounded-3xl bg-gray-50 p-4">
                      <p className="mb-3 flex items-center gap-2 text-sm font-bold text-navy">
                        <i className="ri-contacts-book-2-line text-gold-dark"></i>
                        Contacts
                      </p>
                      <div className="grid gap-2 md:grid-cols-2">
                        {parseContacts(selectedEglise).map((contact, index) => (
                          <div key={`${contact.label}-${index}`} className="rounded-2xl border border-gray-100 bg-white px-3 py-2">
                            <p className="text-xs text-gray-400">{contact.label}</p>
                            <p className="text-sm font-semibold text-navy">{contact.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {selectedEglise.description ? (
                    <div className="rounded-3xl bg-gray-50 p-4">
                      <p className="mb-3 text-sm font-bold text-navy">Description</p>
                      <p className="text-sm leading-7 text-gray-600">
                        {selectedEglise.description || 'Aucune description disponible pour cette église.'}
                      </p>
                    </div>
                  ) : null}

                  {selectedEglise.horaire_bureau ? (
                    <div className="rounded-3xl border border-amber-100 bg-amber-50/70 p-4">
                      <p className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-900">
                        <i className="ri-time-line"></i>
                        Horaire du bureau
                      </p>
                      <p className="text-sm text-amber-800">{selectedEglise.horaire_bureau}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {detailTab === 'map' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-lg font-bold text-navy">Localisation de l’église</p>
                      <p className="text-sm text-gray-500">{buildAddress(selectedEglise)}</p>
                    </div>
                    <a
                      href={getMapExternalUrl(selectedEglise)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-xl border border-navy/20 px-4 py-2 text-sm font-semibold text-navy hover:bg-navy/5"
                    >
                      Ouvrir Maps
                    </a>
                  </div>

                  <div className="h-[340px] overflow-hidden rounded-3xl border border-gray-200 bg-gray-100">
                    <iframe
                      title={`Carte ${selectedEglise.nom}`}
                      src={getMapEmbedUrl(selectedEglise)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                    />
                  </div>

                  <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-600">
                    <p className="mb-2 flex items-center gap-2">
                      <i className="ri-map-pin-2-line text-gold-dark"></i>
                      <span>{buildAddress(selectedEglise)}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-compass-3-line text-emerald-600"></i>
                      <span>{formatGps(selectedEglise)}</span>
                    </p>
                  </div>
                </div>
              ) : null}

              {detailTab === 'team' ? (
                <div className="space-y-3">
                  {parseTeam(selectedEglise).length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center text-sm text-gray-400">
                      Équipe non renseignée pour cette église.
                    </div>
                  ) : (
                    parseTeam(selectedEglise).map((member, index) => (
                      <div key={`${member.name}-${index}`} className="flex items-start gap-4 rounded-3xl border border-gray-100 bg-gray-50 p-4">
                        <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-navy/10">
                          {member.image ? (
                            <img src={member.image} alt={member.name} className="h-full w-full object-cover" />
                          ) : (
                            <i className="ri-user-line text-lg text-navy"></i>
                          )}
                        </div>
                        <div>
                          <p className="text-base font-bold text-navy">{member.name}</p>
                          <p className="text-sm font-semibold text-gold-dark">{member.role}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}

              {detailTab === 'calendar' ? (
                <div className="space-y-5">
                  <div className="rounded-3xl border border-amber-100 bg-amber-50/50 p-4">
                    <p className="mb-3 flex items-center gap-2 text-sm font-bold text-navy">
                      <i className="ri-calendar-event-line text-gold-dark"></i>
                      Horaires des messes
                    </p>
                    {parseSchedule(selectedEglise.horaire_messe).length === 0 && normalizeScheduleCollection(selectedEglise.messe_dimanche).length === 0 ? (
                      <p className="rounded-2xl bg-white px-4 py-3 text-sm text-gray-400">Aucun horaire de messe renseigné.</p>
                    ) : (
                      <div className="space-y-2">
                        {parseSchedule(selectedEglise.horaire_messe).map((item, index) => (
                          <div key={`${item.day}-${item.time}-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                            <span className="text-sm font-semibold text-navy">{item.day}</span>
                            <span className="text-sm text-amber-700">{item.time}</span>
                          </div>
                        ))}
                        {normalizeScheduleCollection(selectedEglise.messe_dimanche)
                          .filter((entry) => typeof entry === 'string' && entry.trim())
                          .map((entry, index) => (
                            <div key={`dimanche-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                              <span className="text-sm font-semibold text-navy">Dimanche</span>
                              <span className="text-sm text-amber-700">{String(entry)}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-3xl border border-emerald-100 bg-emerald-50/50 p-4">
                    <p className="mb-3 flex items-center gap-2 text-sm font-bold text-navy">
                      <i className="ri-chat-heart-line text-emerald-600"></i>
                      Horaires des confessions
                    </p>
                    {parseSchedule(selectedEglise.horaire_confession).length === 0 ? (
                      <p className="rounded-2xl bg-white px-4 py-3 text-sm text-gray-400">Aucun horaire de confession renseigné.</p>
                    ) : (
                      <div className="space-y-2">
                        {parseSchedule(selectedEglise.horaire_confession).map((item, index) => (
                          <div key={`${item.day}-${item.time}-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                            <span className="text-sm font-semibold text-navy">{item.day}</span>
                            <span className="text-sm text-emerald-700">{item.time}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 flex items-center gap-2 text-sm font-bold text-navy">
                      <i className="ri-time-line text-navy"></i>
                      Horaires du secrétariat
                    </p>
                    {parseSchedule(selectedEglise.bureau_schedule).length === 0 ? (
                      <p className="rounded-2xl bg-white px-4 py-3 text-sm text-gray-400">Aucun horaire de bureau détaillé.</p>
                    ) : (
                      <div className="space-y-2">
                        {parseSchedule(selectedEglise.bureau_schedule).map((item, index) => (
                          <div key={`${item.day}-${item.time}-${index}`} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                            <span className="text-sm font-semibold text-navy">{item.day}</span>
                            <span className="text-sm text-slate-700">{item.time}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 border-t border-gray-100 p-5 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  setSelectedEglise(null);
                  openEdit(selectedEglise);
                }}
                className="flex-1 rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white"
              >
                <i className="ri-pencil-line mr-2"></i>
                Modifier
              </button>
              <button
                type="button"
                onClick={() => void handleToggleSuspend(selectedEglise)}
                className="flex-1 rounded-2xl border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50"
              >
                <i className="ri-forbid-2-line mr-2"></i>
                {getChurchStatus(selectedEglise) === 'suspended' ? 'Réactiver' : 'Suspendre'}
              </button>
              <button onClick={() => setSelectedEglise(null)} className="flex-1 rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editingEglise && editForm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setEditingEglise(null); setEditForm(null); }}>
          <div className="w-full max-w-4xl rounded-3xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <div className="px-6 pt-6">
                <h3 className="text-xl font-bold text-navy">Modifier l'église</h3>
                <p className="text-sm text-gray-500">{editingEglise.nom}</p>
              </div>
              <button onClick={() => { setEditingEglise(null); setEditForm(null); }} className="mr-4 mt-4 rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="max-h-[72vh] overflow-y-auto px-6 pb-2">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <input value={editForm.nom} onChange={(event) => setEditForm((current) => ({ ...current, nom: event.target.value }))} placeholder="Nom" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <input value={editForm.diocese} onChange={(event) => setEditForm((current) => ({ ...current, diocese: event.target.value }))} placeholder="Diocèse" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <input value={editForm.ville} onChange={(event) => setEditForm((current) => ({ ...current, ville: event.target.value }))} placeholder="Ville" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <input value={editForm.adresse} onChange={(event) => setEditForm((current) => ({ ...current, adresse: event.target.value }))} placeholder="Adresse" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <input value={editForm.lat} onChange={(event) => setEditForm((current) => ({ ...current, lat: event.target.value }))} placeholder="Latitude" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <input value={editForm.lng} onChange={(event) => setEditForm((current) => ({ ...current, lng: event.target.value }))} placeholder="Longitude" className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
              <select value={editForm.status} onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value }))} className="w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30">
                <option value="active">Active</option>
                <option value="pending">En attente</option>
                <option value="suspended">Suspendue</option>
              </select>
              <textarea value={editForm.description} onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description" className="md:col-span-2 h-28 w-full rounded-xl border border-cream-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4">
              <div className="rounded-2xl border border-gray-100 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-gray-900">Photos de l'église</p>
                    <p className="text-xs text-gray-400">{editForm.images.length}/5 photos. La première photo sera l'image principale.</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => replaceGalleryInputRef.current?.click()}
                      className="rounded-xl border border-navy/20 px-3 py-2 text-xs font-semibold text-navy hover:bg-navy/5"
                    >
                      Remplacer tout
                    </button>
                    <button
                      type="button"
                      onClick={() => editGalleryInputRef.current?.click()}
                      disabled={editForm.images.length >= 5}
                      className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Ajouter
                    </button>
                  </div>
                </div>
                <input
                  ref={replaceGalleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    replaceGalleryFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
                <input
                  ref={editGalleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    addGalleryFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
                <div className="grid grid-cols-2 gap-3">
                  {editForm.images.map((image, index) => (
                    <div key={image.id} className="group relative overflow-hidden rounded-2xl border border-cream-dark bg-gray-50">
                      <button type="button" onClick={() => setPreviewImage(image.url)} className="block h-28 w-full cursor-pointer">
                        <img src={image.url} alt={`Photo ${index + 1}`} className="h-full w-full object-cover" />
                      </button>
                      <div className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">
                        {index === 0 ? 'Principale' : `Photo ${index + 1}`}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(image.id)}
                        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-red-500 shadow-sm hover:bg-red-50"
                        title="Supprimer cette photo"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                  {editForm.images.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => editGalleryInputRef.current?.click()}
                      className="flex h-28 items-center justify-center rounded-2xl border border-dashed border-cream-dark text-sm font-semibold text-gray-400 hover:border-gold hover:text-gold-dark"
                    >
                      <i className="ri-image-add-line mr-2"></i>
                      Ajouter une photo
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            {[
              { title: 'Horaires de messe', key: 'horaire_messe' as const, fallback: blankSchedule },
              { title: 'Horaires de confession', key: 'horaire_confession' as const, fallback: blankSchedule },
              { title: 'Horaires du bureau', key: 'bureau_schedule' as const, fallback: blankSchedule },
            ].map((section) => (
              <div key={section.key} className="mt-5 rounded-2xl border border-gray-100 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-bold text-gray-900">{section.title}</p>
                  <button type="button" onClick={() => setEditForm((current) => ({ ...current, [section.key]: [...current[section.key], section.fallback()] }))} className="rounded-lg bg-gold/15 px-3 py-1.5 text-xs font-semibold text-gold-dark">Ajouter</button>
                </div>
                <div className="space-y-3">
                  {editForm[section.key].map((entry, index) => (
                    <div key={`${section.key}-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <input value={entry.day} onChange={(event) => setEditForm((current) => ({ ...current, [section.key]: current[section.key].map((item, itemIndex) => itemIndex === index ? { ...item, day: event.target.value } : item) }))} placeholder="Jour" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                      <input value={entry.time} onChange={(event) => setEditForm((current) => ({ ...current, [section.key]: current[section.key].map((item, itemIndex) => itemIndex === index ? { ...item, time: event.target.value } : item) }))} placeholder="Heure" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                      <button type="button" onClick={() => setEditForm((current) => ({ ...current, [section.key]: ensureEditableRows(current[section.key].filter((_, itemIndex) => itemIndex !== index), section.fallback) }))} className="rounded-xl px-3 py-2 text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-5 rounded-2xl border border-gray-100 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-gray-900">Contacts</p>
                <button type="button" onClick={() => setEditForm((current) => ({ ...current, contacts: [...current.contacts, blankContact()] }))} className="rounded-lg bg-gold/15 px-3 py-1.5 text-xs font-semibold text-gold-dark">Ajouter</button>
              </div>
              <div className="space-y-3">
                {editForm.contacts.map((contact, index) => (
                  <div key={`contact-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <input value={contact.label} onChange={(event) => setEditForm((current) => ({ ...current, contacts: current.contacts.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) }))} placeholder="Type" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                    <input value={contact.value} onChange={(event) => setEditForm((current) => ({ ...current, contacts: current.contacts.map((item, itemIndex) => itemIndex === index ? { ...item, value: event.target.value } : item) }))} placeholder="Contact" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                    <button type="button" onClick={() => setEditForm((current) => ({ ...current, contacts: ensureEditableRows(current.contacts.filter((_, itemIndex) => itemIndex !== index), blankContact) }))} className="rounded-xl px-3 py-2 text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-gray-100 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-gray-900">Équipe</p>
                <button type="button" onClick={() => setEditForm((current) => ({ ...current, equipe: [...current.equipe, blankTeamMember()] }))} className="rounded-lg bg-gold/15 px-3 py-1.5 text-xs font-semibold text-gold-dark">Ajouter</button>
              </div>
              <div className="space-y-3">
                {editForm.equipe.map((member, index) => (
                  <div key={`team-${index}`} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                    <input value={member.name} onChange={(event) => setEditForm((current) => ({ ...current, equipe: current.equipe.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) }))} placeholder="Nom" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                    <input value={member.role} onChange={(event) => setEditForm((current) => ({ ...current, equipe: current.equipe.map((item, itemIndex) => itemIndex === index ? { ...item, role: event.target.value } : item) }))} placeholder="Rôle" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                    <input value={member.image ?? ''} onChange={(event) => setEditForm((current) => ({ ...current, equipe: current.equipe.map((item, itemIndex) => itemIndex === index ? { ...item, image: event.target.value } : item) }))} placeholder="Image" className="rounded-xl border border-cream-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30" />
                    <button type="button" onClick={() => setEditForm((current) => ({ ...current, equipe: ensureEditableRows(current.equipe.filter((_, itemIndex) => itemIndex !== index), blankTeamMember) }))} className="rounded-xl px-3 py-2 text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line"></i></button>
                  </div>
                ))}
              </div>
            </div>
            </div>
            <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button onClick={() => { setEditingEglise(null); setEditForm(null); }} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">Annuler</button>
              <button onClick={() => void handleUpdateChurch()} disabled={submitting} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-70">
                {submitting ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {previewImage ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-h-[90vh] w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow-lg hover:bg-white"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <img src={previewImage} alt="Aperçu de la photo" className="max-h-[90vh] w-full rounded-3xl object-contain shadow-2xl" />
          </div>
        </div>
      ) : null}
    </div>
  );
}
